// src/AnA/CompleteGoogleProfile.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getFirestore, doc, setDoc, collection, getDocs } from "firebase/firestore";
import { Code, User, AlertCircle, CheckCircle } from "lucide-react";
import "./AuthStyles.css";

const CompleteGoogleProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [formData, setFormData] = useState({
    name: "",
    program: "",
    yearOfStudy: ""
  });
  
  const [programs, setPrograms] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [success, setSuccess] = useState(false);

  // Get user data from location state
  const googleUser = location.state?.user;

  useEffect(() => {
    // Redirect if no user data passed
    if (!googleUser) {
      navigate("/login", { replace: true });
      return;
    }

    // Fetch programs
    const fetchPrograms = async () => {
      try {
        const db = getFirestore();
        const programsRef = collection(db, "programs");
        const snapshot = await getDocs(programsRef);
        const programsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().name,
        }));
        setPrograms(programsList);
        
        // Pre-fill name from Google account
        setFormData((prev) => ({
          ...prev,
          name: googleUser.displayName || "",
        }));
      } catch (err) {
        console.error("Error fetching programs:", err);
        setError("Failed to load programs. Please try again.");
      } finally {
        setPageLoading(false);
      }
    };

    fetchPrograms();
  }, [googleUser, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    // Validate inputs
    if (!formData.name.trim()) {
      setError("Full name is required");
      return;
    }

    if (!formData.program) {
      setError("Please select a program");
      return;
    }

    if (!formData.yearOfStudy) {
      setError("Please select year of study");
      return;
    }

    setLoading(true);

    try {
      const db = getFirestore();
      const userRef = doc(db, "users", googleUser.uid);

      // Create user document with complete information
      await setDoc(userRef, {
        email: googleUser.email,
        name: formData.name,
        role: "viewer",
        createdAt: new Date().toISOString(),
        subscriptionStatus: "inactive",
        subscriptionStart: new Date().toISOString(),
        subscriptionEnd: null,
        program: formData.program,
        yearOfStudy: formData.yearOfStudy,
        authProvider: "google",
        profileCompleted: true,
        uid: googleUser.uid,
      });

      // Store user info in localStorage
      localStorage.setItem("userEmail", googleUser.email);
      localStorage.setItem("userId", googleUser.uid);
      localStorage.setItem("userRole", "viewer");

      setSuccess(true);

      // Redirect to viewer after successful save
      setTimeout(() => {
        navigate("/viewer", { replace: true });
      }, 1500);
    } catch (err) {
      console.error("Error saving user data:", err);
      setError("Failed to save profile. Please try again.");
      setLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <div className="auth-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <Code size={36} className="logo-icon" />
          <h1>Complete Your Profile</h1>
          <p>Just a few more details to get started</p>
        </div>

        {error && (
          <div className="error-message">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="success-message">
            <CheckCircle size={18} />
            <span>Profile completed successfully! Redirecting...</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="input-group">
            <User size={20} className="input-icon" />
            <input
              type="text"
              placeholder="Full Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              disabled={loading}
            />
          </div>

          <div className="input-dropdown">
            <label htmlFor="program">Program</label>
            <select
              id="program"
              name="program"
              value={formData.program}
              onChange={handleInputChange}
              required
              disabled={loading}
            >
              <option value="">Select Program</option>
              {programs.map((program) => (
                <option key={program.id} value={program.name}>
                  {program.name}
                </option>
              ))}
            </select>
          </div>

          <div className="input-dropdown">
            <label htmlFor="yearOfStudy">Year of Study</label>
            <select
              id="yearOfStudy"
              name="yearOfStudy"
              value={formData.yearOfStudy}
              onChange={handleInputChange}
              required
              disabled={loading}
            >
              <option value="">Select Year</option>
              <option value="1st year">1st Year</option>
              <option value="2nd year">2nd Year</option>
              <option value="3rd year">3rd Year</option>
              <option value="4th year">4th Year</option>
              <option value="5th year">5th Year</option>
              <option value="6th year">6th Year</option>
            </select>
          </div>

          <button type="submit" className="auth-button" disabled={loading}>
            {loading ? "Saving Profile..." : "Complete Registration"}
          </button>
        </form>

        <div className="profile-info">
          <p>
            <strong>Email:</strong> {googleUser?.email}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CompleteGoogleProfile;
